/**
 * Translate a lat/lng path by a meters offset. Positive dx = east,
 * positive dy = north. Uses the equirectangular approximation, which is
 * accurate to < 1cm for the offsets we'd apply here (typically <10m).
 */

const METERS_PER_DEGREE_LAT = 111320;

export interface OffsetMeters {
  dx: number;
  dy: number;
}

export function translateLatLng(
  point: { lat: number; lng: number },
  offset: OffsetMeters,
): { lat: number; lng: number } {
  if (!offset || (!offset.dx && !offset.dy)) return point;
  const dLat = offset.dy / METERS_PER_DEGREE_LAT;
  const dLng =
    offset.dx / (METERS_PER_DEGREE_LAT * Math.cos((point.lat * Math.PI) / 180));
  return { lat: point.lat + dLat, lng: point.lng + dLng };
}

export function translatePath(
  path: Array<{ lat: number; lng: number }>,
  offset: OffsetMeters,
): Array<{ lat: number; lng: number }> {
  if (!offset || (!offset.dx && !offset.dy)) return path;
  return path.map((p) => translateLatLng(p, offset));
}

/** Convert a screen-pixel delta to meters at a given map zoom level and lat.
 *  Approximation valid for the sub-pixel to a few-hundred-pixel range. */
export function pixelsToMeters(
  pxDx: number,
  pxDy: number,
  lat: number,
  zoom: number,
): OffsetMeters {
  const metersPerPixel =
    (2 * Math.PI * 6378137 * Math.cos((lat * Math.PI) / 180)) /
    (256 * Math.pow(2, zoom));
  return {
    dx: pxDx * metersPerPixel,
    dy: -pxDy * metersPerPixel,
  };
}

export function metersToFeet(m: number): number {
  return m * 3.28084;
}

export function feetToMeters(ft: number): number {
  return ft / 3.28084;
}
